import { IssueState } from "@boark/shared/types/issue";
import { IssueWithCreatedByAndLinksAndChildrenAndParentAndRegisters } from "@boark/shared/types/issue-with-created-by-and-links-and-children-and-parent-and-registers";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import {
  Outlet,
  Route,
  Routes,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

import { Button } from "../../components/button";
import { Confirm } from "../../components/confirm";
import { H1 } from "../../components/h1";
import { Column, Filter, Table } from "../../components/table";
import { useGetIssues } from "../../queries/issue";
import { deleteIssue, uniqueSortedTagSuggestions } from "../../services/issue";
import { useLocale } from "../../stores/locale";
import { formatDate } from "../../utils/date";
import { navigateBack } from "../../utils/navigate-back";
import { IssueCreate } from "./issue-create";
import { IssueEdit } from "./issue-edit";
import { IssueView } from "./issue-view";

type IssueRow = IssueWithCreatedByAndLinksAndChildrenAndParentAndRegisters;

export function IssueList() {
  const { locale: t } = useLocale();
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: issues, isLoading: isIssuesLoading } = useGetIssues();

  function updateSearchParams(params: Record<string, string>) {
    setSearchParams({
      ...Object.fromEntries(searchParams.entries()),
      ...params,
    });
  }

  const stateLabel = useMemo(
    () => (state: IssueState) => {
      switch (state) {
        case IssueState.TODO:
          return t.issue.stateTodo;
        case IssueState.IN_PROGRESS:
          return t.issue.stateInProgress;
        case IssueState.DONE:
          return t.issue.stateDone;
        case IssueState.WONTDO:
          return t.issue.stateWontdo;
      }
    },
    [t.issue],
  );

  const stateFilterOptions = useMemo(
    () => [
      { label: t.issue.stateTodo, value: IssueState.TODO },
      { label: t.issue.stateInProgress, value: IssueState.IN_PROGRESS },
      { label: t.issue.stateDone, value: IssueState.DONE },
      { label: t.issue.stateWontdo, value: IssueState.WONTDO },
    ],
    [t.issue],
  );

  const tagFilterOptions = useMemo(
    () =>
      uniqueSortedTagSuggestions(issues).map((tag) => ({
        label: tag,
        value: tag,
      })),
    [issues],
  );

  return (
    <>
      <Routes>
        <Route element={<IssueCreate />} path="/create" />
        <Route element={<IssueDelete />} path="/:id/delete" />
        <Route element={<IssueEdit />} path="/:id/edit" />
        <Route element={<IssueView />} path="/:id" />
      </Routes>
      <Outlet />
      <div className="w-full min-h-full overflow-x-auto">
        <div className="flex justify-between items-center">
          <H1>{t.issue.issues}</H1>
          <Button route="/admin/issue/list/create">{t.issue.create}</Button>
        </div>
        <Table
          data={issues || []}
          loading={isIssuesLoading}
          editRouteButton={(row) =>
            `/admin/issue/list/${row.identifier}/edit`
          }
          filterBy={
            JSON.parse(searchParams.get("filterBy") || "[]") as Filter[]
          }
          locale={{ yes: t.yes, no: t.no, actions: t.action.actions, empty: t.empty }}
          removeRouteButton={(row) =>
            `/admin/issue/list/${row.identifier}/delete`
          }
          sortBy={searchParams.get("sortBy") || "createdAt"}
          sortByDirection={
            searchParams.get("sortByDirection") as "asc" | "desc"
          }
          viewRouteButton={(row) => `/admin/issue/list/${row.identifier}`}
          onFilter={(value: Filter[]) => {
            updateSearchParams({
              filterBy: JSON.stringify(value),
            });
          }}
          onSort={(field, direction) => {
            updateSearchParams({
              sortBy: field,
              sortByDirection: direction,
            });
          }}
        >
          <Column field="title">{t.issue.title}</Column>
          <Column
            body={(row: IssueRow) =>
              row.tags.length > 0 ? row.tags.join(", ") : "-"
            }
            field="tags"
            filterableBy="select"
            filterOptions={() => tagFilterOptions}
          >
            {t.issue.tags}
          </Column>
          <Column
            body={(row: IssueRow) => stateLabel(row.state)}
            field="state"
            filterableBy="select"
            filterOptions={() => stateFilterOptions}
          >
            {t.issue.state}
          </Column>
          <Column
            body={(row: IssueRow) => row.createdBy.email}
            field="createdBy.email"
            filterableBy="text"
          >
            {t.issue.createdBy}
          </Column>
          <Column
            body={(row: IssueRow) => formatDate(row.createdAt)}
            field="createdAt"
            filterableBy="date"
          >
            {t.issue.createdAt}
          </Column>
          <Column
            body={(row: IssueRow) => formatDate(row.updatedAt)}
            field="updatedAt"
            filterableBy="date"
          >
            {t.issue.updatedAt}
          </Column>
        </Table>
      </div>
    </>
  );
}

function IssueDelete() {
  const navigate = useNavigate();
  const locale = useLocale((state) => state);
  const queryClient = useQueryClient();
  const { id } = useParams();

  if (!id) {
    throw new Error("Issue identifier is required");
  }

  const [loading, setLoading] = useState(false);

  const closeModal = () => {
    navigateBack(navigate, "/admin/issue/list");
  };

  return (
    <Confirm
      header={locale.get("issue.confirmDelete")}
      loading={loading}
      onClose={closeModal}
      onConfirm={() => {
        (async () => {
          setLoading(true);
          try {
            await deleteIssue(id);
            await Promise.all([
              queryClient.invalidateQueries({ queryKey: ["issues"] }),
              queryClient.invalidateQueries({ queryKey: ["issue", id] }),
            ]);
          } finally {
            setLoading(false);
            closeModal();
          }
        })();
      }}
    />
  );
}
